import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import hideBarWhiteIcon from '../../../../assets/images/hide-bar-white.svg';
import CandidateCard from '../CandidateCard';
import AdminService from '../../../../services/AdminService';
import { ALL_COMMON_KEYWORDS, CERTIFICATIONS } from '../searchKeywords';
import useOnClickOutside from '../../../../hooks/useOnClickOutside';
import useSpeechRecognition from '../../../../hooks/useSpeechRecognition';
import classes from './styles.module.scss';

// Feature flag for selection functionality
const ENABLE_SELECTION = false;

export default function SearchArea({
  isLeftPanelCollapsed,
  onToggleLeftPanel,
  searchInput,
  setSearchInput,
  searchChips,
  onAddChip,
  onRemoveChip,
  results,
  isLoading,
  pagination,
  setPagination,
  selectedCandidates,
  onToggleCandidateSelection,
  onSearch,
  sortBy,
  setSortBy,
  onSaveSearch,
  activeSavedSearch,
  onRevertToSaved,
  onClearActiveSavedSearch,
  activeJob,
  onClearActiveJob,
}) {
  const { t } = useTranslation();
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [skills, setSkills] = useState([]);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);

  const dropdownRef = useRef();
  const inputRef = useRef();
  const actionMenuRef = useRef();
  const actionButtonRef = useRef();
  const sortMenuRef = useRef();
  const sortButtonRef = useRef();
  const moreMenuRef = useRef();
  const moreButtonRef = useRef();
  const shouldTriggerSearchAfterChipAddRef = useRef(false);
  const previousChipCountRef = useRef(searchChips.length);

  // Speech recognition
  const {
    isListening,
    transcript,
    interimTranscript,
    error: speechError,
    isSupported: isSpeechSupported,
    startListening,
    stopListening,
    reset: resetSpeech,
  } = useSpeechRecognition();

  // Close dropdown on click outside
  const handleCloseDropdown = useCallback(() => {
    setShowDropdown(false);
  }, []);

  const handleCloseActionMenu = useCallback(() => {
    setActionMenuOpen(false);
  }, []);

  const handleCloseSortMenu = useCallback(() => {
    setSortMenuOpen(false);
  }, []);

  const handleCloseMoreMenu = useCallback(() => {
    setMoreMenuOpen(false);
  }, []);

  useOnClickOutside(dropdownRef, handleCloseDropdown, inputRef);
  useOnClickOutside(actionMenuRef, handleCloseActionMenu, actionButtonRef);
  useOnClickOutside(sortMenuRef, handleCloseSortMenu, sortButtonRef);
  useOnClickOutside(moreMenuRef, handleCloseMoreMenu, moreButtonRef);

  // Load search history from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('intelSearchHistory');
      if (stored) {
        setSearchHistory(JSON.parse(stored));
      }
    } catch (error) {
      // Ignore parse errors
    }
  }, []);

  // Save search history to localStorage
  const saveToHistory = useCallback((keyword) => {
    setSearchHistory((prevHistory) => {
      const updated = [
        keyword,
        ...prevHistory.filter(
          (item) => item.toLowerCase() !== keyword.toLowerCase()
        ),
      ].slice(0, 20); // Keep last 20 items
      localStorage.setItem('intelSearchHistory', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Clear search history
  const clearHistory = useCallback(() => {
    setSearchHistory([]);
    localStorage.removeItem('intelSearchHistory');
  }, []);

  // Remove single history item
  const removeHistoryItem = useCallback((keyword) => {
    setSearchHistory((prevHistory) => {
      const updated = prevHistory.filter((item) => item !== keyword);
      localStorage.setItem('intelSearchHistory', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Fetch all skills from DB on mount (full objects with ID and name)
  useEffect(() => {
    const fetchSkills = async () => {
      setLoadingSkills(true);
      try {
        const response = await AdminService.getSkills({
          orderBy: 'name',
          sortOrder: 'asc',
          pageSize: 1000, // Get all skills
        });

        // Store full skill objects (id, name) for exact matching
        setSkills(response?.data || []);
      } catch (error) {
        // Silently fail - will use common keywords only
      } finally {
        setLoadingSkills(false);
      }
    };

    fetchSkills();
  }, []);

  // Update search input with interim transcript (real-time feedback)
  useEffect(() => {
    if (isListening && interimTranscript) {
      setSearchInput(interimTranscript);
    }
  }, [interimTranscript, isListening, setSearchInput]);

  // Create suggestions: skill objects + certifications + common keywords
  const allSuggestions = useMemo(() => {
    // Skills are objects with { id, name }
    const skillSuggestions = skills.map((skill) => ({
      type: 'skill',
      value: skill.name,
      id: skill.id,
    }));

    // Certifications from known list (exact match on backend)
    const certificationSuggestions = CERTIFICATIONS.map((cert) => ({
      type: 'certification',
      value: cert,
    }));

    // Other common keywords (contains match)
    const otherKeywords = ALL_COMMON_KEYWORDS.filter(
      (kw) => !CERTIFICATIONS.includes(kw)
    );
    const keywordSuggestions = otherKeywords.map((keyword) => ({
      type: 'keyword',
      value: keyword,
    }));

    return [
      ...skillSuggestions,
      ...certificationSuggestions,
      ...keywordSuggestions,
    ].sort((a, b) =>
      a.value.toLowerCase().localeCompare(b.value.toLowerCase())
    );
  }, [skills]);

  // Filter and rank suggestions, split by type
  const { skillSuggestions, certificationSuggestions } = useMemo(() => {
    if (!searchInput || searchInput.length < 1) {
      return { skillSuggestions: [], certificationSuggestions: [] };
    }

    const searchLower = searchInput.toLowerCase().trim();

    // Helper function to score a suggestion
    const scoreSuggestion = (suggestion) => {
      const suggestionValueLower = suggestion.value.toLowerCase();

      // Don't show already selected chips
      if (
        searchChips.some((chip) => {
          const chipValue =
            typeof chip === 'string' ? chip : chip.value || chip;
          return chipValue.toLowerCase() === suggestionValueLower;
        })
      ) {
        return null;
      }

      // Check if matches
      if (!suggestionValueLower.includes(searchLower)) {
        return null;
      }

      // Calculate relevance score (lower is better)
      let score = 0;

      // 1. Exact match (highest priority)
      if (suggestionValueLower === searchLower) {
        score = 0;
      }
      // 2. Word boundary match (e.g., "C" matches "C#" or "C++")
      else if (
        suggestionValueLower.startsWith(`${searchLower} `) ||
        suggestionValueLower.startsWith(`${searchLower}#`) ||
        suggestionValueLower.startsWith(`${searchLower}+`)
      ) {
        score = 50;
      }
      // 3. Starts with search term
      else if (suggestionValueLower.startsWith(searchLower)) {
        score = 100;
      }
      // 4. Contains search term
      else {
        score = 200;
      }

      // Add length penalty (prefer shorter matches)
      score += suggestion.value.length * 0.1;

      return { suggestion, score };
    };

    // Separate by type and score
    const skillMatches = [];
    const certificationMatches = [];
    const keywordMatches = [];

    allSuggestions.forEach((suggestion) => {
      const scored = scoreSuggestion(suggestion);
      if (scored) {
        if (suggestion.type === 'skill') {
          skillMatches.push(scored);
        } else if (suggestion.type === 'certification') {
          certificationMatches.push(scored);
        } else {
          keywordMatches.push(scored);
        }
      }
    });

    // Sort each group by score
    skillMatches.sort((a, b) => a.score - b.score);
    certificationMatches.sort((a, b) => a.score - b.score);
    keywordMatches.sort((a, b) => a.score - b.score);

    // Deduplicate: remove skills that have exact match in certifications
    const certValues = new Set(
      certificationMatches.map((c) => c.suggestion.value.toLowerCase())
    );
    const dedupedSkills = skillMatches.filter(
      (s) => !certValues.has(s.suggestion.value.toLowerCase())
    );

    // Take top 5 from each category
    const topSkills = dedupedSkills.slice(0, 5).map((item) => item.suggestion);
    const topCerts = certificationMatches
      .slice(0, 5)
      .map((item) => item.suggestion);

    return {
      skillSuggestions: topSkills,
      certificationSuggestions: topCerts,
    };
  }, [searchInput, allSuggestions, searchChips]);

  // Filter history based on input
  const filteredHistory = useMemo(() => {
    if (!searchInput || searchInput.length < 1) {
      return searchHistory.slice(0, 10);
    }

    const searchLower = searchInput.toLowerCase().trim();
    return searchHistory
      .filter((keyword) => {
        const keywordLower = keyword.toLowerCase();
        // Don't show already selected chips
        if (
          searchChips.some((chip) => {
            const chipValue =
              typeof chip === 'string' ? chip : chip.value || chip;
            return chipValue.toLowerCase() === keywordLower;
          })
        ) {
          return false;
        }
        return keywordLower.includes(searchLower);
      })
      .slice(0, 5);
  }, [searchInput, searchHistory, searchChips]);

  const handleAddChip = useCallback(
    (keywordOrSuggestion) => {
      // If it's already a suggestion object from autocomplete, use it directly
      if (
        typeof keywordOrSuggestion === 'object' &&
        keywordOrSuggestion !== null &&
        keywordOrSuggestion.type
      ) {
        onAddChip(keywordOrSuggestion);
        saveToHistory(keywordOrSuggestion.value);
        setSearchInput('');
        setShowDropdown(false);
        return;
      }

      // Otherwise, it's free text - check if it exactly matches a skill or certification
      const keyword =
        typeof keywordOrSuggestion === 'string'
          ? keywordOrSuggestion
          : keywordOrSuggestion.value || keywordOrSuggestion;

      const matchingSkill = skills.find(
        (skill) => skill.name.toLowerCase() === keyword.toLowerCase()
      );

      if (matchingSkill) {
        // Exact skill match - add as skill chip
        onAddChip({
          type: 'skill',
          value: matchingSkill.name,
          id: matchingSkill.id,
        });
      } else {
        // Check if it matches a certification (exact match, case-insensitive)
        const matchingCertification = CERTIFICATIONS.find(
          (cert) => cert.toLowerCase() === keyword.toLowerCase()
        );

        if (matchingCertification) {
          // Exact certification match - add as certification chip
          onAddChip({
            type: 'certification',
            value: matchingCertification,
          });
        } else {
          // Free text - add as keyword chip
          onAddChip({
            type: 'keyword',
            value: keyword,
          });
        }
      }

      saveToHistory(keyword);
      setSearchInput('');
      setShowDropdown(false);
    },
    [onAddChip, saveToHistory, skills, setSearchInput]
  );

  // Handle speech recognition transcript
  useEffect(() => {
    if (transcript) {
      // Add the transcript as a chip
      handleAddChip(transcript);
      resetSpeech();
    }
  }, [transcript, handleAddChip, resetSpeech]);

  // Trigger search after chip is added (when run button was clicked) or removed
  useEffect(() => {
    const currentCount = searchChips.length;
    const previousCount = previousChipCountRef.current;
    const chipCountChanged = currentCount !== previousCount;

    // Case 1: Chip was added via run button
    if (
      shouldTriggerSearchAfterChipAddRef.current &&
      currentCount > previousCount
    ) {
      // Reset the flag first to prevent multiple triggers
      shouldTriggerSearchAfterChipAddRef.current = false;
      // Update previous count
      previousChipCountRef.current = currentCount;
      // Trigger search after state has updated
      // Use setTimeout to ensure state update is processed
      setTimeout(() => {
        onSearch();
      }, 0);
      return;
    }

    // Case 2: Chip was removed - always trigger search if we have results or chips
    if (chipCountChanged && currentCount < previousCount) {
      // Update previous count
      previousChipCountRef.current = currentCount;
      // Trigger search after chip removal
      // Only trigger if we have results (search was performed) or still have chips
      if (results.length > 0 || currentCount > 0) {
        setTimeout(() => {
          onSearch();
        }, 0);
      }
      return;
    }

    // Update previous count for other cases (no action needed)
    if (chipCountChanged) {
      previousChipCountRef.current = currentCount;
    }
  }, [searchChips, onSearch, results.length]);

  const handleSearchKeyDown = useCallback(
    (e) => {
      // Enter: Add chip
      if (e.key === 'Enter' && searchInput.trim()) {
        e.preventDefault();
        handleAddChip(searchInput.trim());
        return;
      }

      // Escape: Close dropdown
      if (e.key === 'Escape') {
        setShowDropdown(false);
        return;
      }

      // Down Arrow: Show dropdown
      if (e.key === 'ArrowDown' && !showDropdown) {
        setShowDropdown(true);
      }
    },
    [searchInput, handleAddChip, showDropdown]
  );

  const handleInputChange = useCallback(
    (e) => {
      const { value } = e.target;
      setSearchInput(value);
      // Show dropdown when typing (at least 1 char)
      if (value.length >= 1) {
        setShowDropdown(true);
      } else if (value.length === 0 && searchHistory.length > 0) {
        // Show history when empty
        setShowDropdown(true);
      } else {
        setShowDropdown(false);
      }
    },
    [searchHistory.length, setSearchInput]
  );

  const handleInputFocus = useCallback(() => {
    // Show dropdown on focus if we have history or typed text
    if (searchHistory.length > 0 || searchInput.length > 0) {
      setShowDropdown(true);
    }
  }, [searchHistory.length, searchInput.length]);

  const handleSuggestionClick = useCallback(
    (keyword) => {
      handleAddChip(keyword);
      inputRef.current?.focus();
    },
    [handleAddChip]
  );

  // Handle microphone button click
  const handleMicrophoneClick = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  const actionOptions = useMemo(
    () => [
      { label: t('intelSearch.searchArea.actions.invite'), value: 'invite' },
      { label: t('intelSearch.searchArea.actions.edit'), value: 'edit' },
      { label: t('intelSearch.searchArea.actions.submit'), value: 'submit' },
      { label: t('intelSearch.searchArea.actions.delete'), value: 'delete' },
    ],
    [t]
  );

  const sortOptions = useMemo(() => {
    const baseOptions = [
      {
        label: t('intelSearch.searchArea.sortOptions.keywordMatch'),
        value: 'totalMatchCount:desc',
      },
      {
        label: t('intelSearch.searchArea.sortOptions.lastName'),
        value: 'userProfile.lastName:asc',
      },
      {
        label: t('intelSearch.searchArea.sortOptions.lastViewed'),
        value: 'viewedAt:desc',
      },
      {
        label: t('intelSearch.searchArea.sortOptions.lastActivity'),
        value: 'lastLoginAt:desc',
      },
      {
        label: t('intelSearch.searchArea.sortOptions.source'),
        value: 'sourceName:asc',
      },
    ];

    // Add "Best Match" option when jobId exists, and make it first
    if (activeJob) {
      return [
        {
          label: t('intelSearch.searchArea.sortOptions.bestMatch'),
          value: 'jobMatchPercent:desc',
        },
        ...baseOptions,
      ];
    }

    return baseOptions;
  }, [t, activeJob]);

  // Get current sort label
  const currentSortLabel =
    sortOptions.find((option) => option.value === sortBy)?.label ||
    t('intelSearch.searchArea.sort');

  return (
    <div className={classes.SearchArea}>
      {/* Search Bar */}
      <div className={classes.searchBarContainer}>
        {isLeftPanelCollapsed && (
          <button
            type="button"
            className={classes.sidebarToggle}
            onClick={onToggleLeftPanel}
            title={t('intelSearch.leftPanel.expand')}
          >
            <img src={hideBarWhiteIcon} alt="Expand panel" />
          </button>
        )}

        <div className={classes.searchInputWrapper} ref={inputRef}>
          <div className={classes.searchInput}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={classes.searchIcon}
            >
              <circle
                cx="9"
                cy="9"
                r="6"
                stroke="#C1C5D6"
                strokeWidth="2"
                fill="none"
              />
              <path
                d="M14 14L17 17"
                stroke="#C1C5D6"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <input
              type="text"
              placeholder={t('intelSearch.searchArea.placeholder')}
              value={searchInput}
              onChange={handleInputChange}
              onKeyDown={handleSearchKeyDown}
              onFocus={handleInputFocus}
              disabled={isListening}
            />
            {loadingSkills && !isListening && (
              <span className={classes.loadingIndicator}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={classes.spinner}
                >
                  <circle
                    cx="8"
                    cy="8"
                    r="6"
                    stroke="#C1C5D6"
                    strokeWidth="2"
                    strokeDasharray="30 10"
                  />
                </svg>
              </span>
            )}
            {isSpeechSupported && (
              <button
                type="button"
                className={classNames(classes.microphoneButton, {
                  [classes.listening]: isListening,
                  [classes.error]: speechError,
                })}
                onClick={handleMicrophoneClick}
                title={
                  isListening
                    ? t('intelSearch.searchArea.stopListening')
                    : t('intelSearch.searchArea.startListening')
                }
              >
                {isListening ? (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="5"
                      y="4"
                      width="10"
                      height="12"
                      rx="5"
                      fill="#FF4444"
                      className={classes.micPulse}
                    />
                    <path
                      d="M10 16V18M10 18H7M10 18H13"
                      stroke="#FF4444"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="7"
                      y="3"
                      width="6"
                      height="10"
                      rx="3"
                      stroke="#C1C5D6"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M4 10C4 13 6.5 15 10 15C13.5 15 16 13 16 10M10 15V18M10 18H7M10 18H13"
                      stroke="#C1C5D6"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
              </button>
            )}
          </div>

          {/* Combined Suggestions & History Dropdown */}
          {showDropdown &&
            (skillSuggestions.length > 0 ||
              certificationSuggestions.length > 0 ||
              filteredHistory.length > 0) && (
              <div className={classes.searchDropdown} ref={dropdownRef}>
                {/* Skills Section */}
                {skillSuggestions.length > 0 && (
                  <div className={classes.dropdownSection}>
                    <div className={classes.sectionHeader}>
                      <span className={classes.sectionTitle}>
                        {t('intelSearch.searchArea.skills')} ✓
                      </span>
                    </div>
                    <ul className={classes.itemsList}>
                      {skillSuggestions.map((suggestion) => (
                        <li
                          key={`skill-${suggestion.id}`}
                          className={classes.dropdownItem}
                        >
                          <button
                            type="button"
                            className={classes.itemButton}
                            onClick={() => handleSuggestionClick(suggestion)}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className={classes.itemIcon}
                            >
                              <circle
                                cx="8"
                                cy="8"
                                r="6.5"
                                stroke="#00E4AC"
                                strokeWidth="1"
                              />
                              <path
                                d="M5 8L7 10L11 6"
                                stroke="#00E4AC"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <span>{suggestion.value}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Certifications Section */}
                {certificationSuggestions.length > 0 && (
                  <div className={classes.dropdownSection}>
                    <div className={classes.sectionHeader}>
                      <span className={classes.sectionTitle}>
                        {t('intelSearch.searchArea.certifications')} 🎓
                      </span>
                    </div>
                    <ul className={classes.itemsList}>
                      {certificationSuggestions.map((suggestion) => (
                        <li
                          key={`cert-${suggestion.value}`}
                          className={classes.dropdownItem}
                        >
                          <button
                            type="button"
                            className={classes.itemButton}
                            onClick={() => handleSuggestionClick(suggestion)}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className={classes.itemIcon}
                            >
                              <circle
                                cx="8"
                                cy="8"
                                r="6.5"
                                stroke="#FF9500"
                                strokeWidth="1"
                              />
                              <path
                                d="M5 8L7 10L11 6"
                                stroke="#FF9500"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <span>{suggestion.value}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* History Section */}
                {filteredHistory.length > 0 && (
                  <div className={classes.dropdownSection}>
                    <div className={classes.sectionHeader}>
                      <span className={classes.sectionTitle}>
                        {t('intelSearch.searchArea.searchHistory')}
                      </span>
                      <button
                        type="button"
                        className={classes.clearHistoryButton}
                        onClick={clearHistory}
                      >
                        {t('intelSearch.searchArea.clearHistory')}
                      </button>
                    </div>
                    <ul className={classes.itemsList}>
                      {filteredHistory.map((keyword) => (
                        <li key={keyword} className={classes.dropdownItem}>
                          <button
                            type="button"
                            className={classes.itemButton}
                            onClick={() => handleSuggestionClick(keyword)}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className={classes.itemIcon}
                            >
                              <circle
                                cx="8"
                                cy="8"
                                r="6.5"
                                stroke="#C1C5D6"
                                strokeWidth="1"
                              />
                              <path
                                d="M8 4v4l3 2"
                                stroke="#C1C5D6"
                                strokeWidth="1"
                                strokeLinecap="round"
                              />
                            </svg>
                            <span>{keyword}</span>
                          </button>
                          <button
                            type="button"
                            className={classes.removeButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeHistoryItem(keyword);
                            }}
                            title={t(
                              'intelSearch.searchArea.removeFromHistory'
                            )}
                          >
                            ×
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
        </div>

        <div className={classes.searchActions}>
          <button
            type="button"
            className={classes.runButton}
            onClick={() => {
              // Convert any remaining input to a keyword chip before searching
              if (searchInput.trim()) {
                // Set flag to trigger search after chip is added
                shouldTriggerSearchAfterChipAddRef.current = true;
                handleAddChip(searchInput.trim());
              } else {
                // No input to convert, just run search
                onSearch();
              }
            }}
            disabled={isLoading}
          >
            {isLoading
              ? t('intelSearch.searchArea.searching')
              : t('intelSearch.searchArea.run')}
          </button>
          <button
            type="button"
            className={classes.saveButton}
            onClick={onSaveSearch}
            disabled={!results || results.length === 0}
            title={
              results && results.length > 0
                ? t('intelSearch.searchArea.saveSearch')
                : t('intelSearch.searchArea.noResultsToSave')
            }
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 2H5C3.89543 2 3 2.89543 3 4V16C3 17.1046 3.89543 18 5 18H15C16.1046 18 17 17.1046 17 16V4C17 2.89543 16.1046 2 15 2Z"
                stroke="#242833"
                strokeWidth="1.5"
              />
              <path d="M7 2V7H13V2" stroke="#242833" strokeWidth="1.5" />
              <path d="M7 13H13" stroke="#242833" strokeWidth="1.5" />
            </svg>
          </button>
          <div className={classes.moreButtonContainer}>
            <button
              ref={moreButtonRef}
              type="button"
              className={classes.moreButton}
              onClick={() => setMoreMenuOpen(!moreMenuOpen)}
              disabled={!activeSavedSearch}
              title={
                activeSavedSearch
                  ? t('intelSearch.searchArea.moreOptions')
                  : t('intelSearch.searchArea.moreOptionsDisabled')
              }
            >
              <svg
                width="4"
                height="16"
                viewBox="0 0 4 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="2" cy="2" r="2" fill="#242833" />
                <circle cx="2" cy="8" r="2" fill="#242833" />
                <circle cx="2" cy="14" r="2" fill="#242833" />
              </svg>
            </button>

            {moreMenuOpen && (
              <div ref={moreMenuRef} className={classes.moreMenu}>
                <button
                  type="button"
                  className={classes.moreMenuItem}
                  onClick={() => {
                    onSaveSearch();
                    setMoreMenuOpen(false);
                  }}
                >
                  {t('intelSearch.searchArea.saveAsNewList')}
                </button>
                <button
                  type="button"
                  className={classes.moreMenuItem}
                  onClick={() => {
                    onRevertToSaved();
                    setMoreMenuOpen(false);
                  }}
                >
                  {t('intelSearch.searchArea.revert')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chips Row - Keywords on left, Saved Search and Job on right */}
      {(searchChips.length > 0 || activeSavedSearch || activeJob) && (
        <div className={classes.chipsRow}>
          {/* Keyword Chips */}
          <div className={classes.chipsContainer}>
            {searchChips.length > 0 &&
              searchChips.map((chip) => {
                const chipValue =
                  typeof chip === 'string' ? chip : chip.value || chip;

                let chipKey;
                if (typeof chip === 'string') {
                  chipKey = chip;
                } else if (chip.type === 'skill') {
                  chipKey = `skill-${chip.id}`;
                } else if (chip.type === 'certification') {
                  chipKey = `cert-${chip.value}`;
                } else {
                  chipKey = `keyword-${chip.value}`;
                }

                return (
                  <div key={chipKey} className={classes.chip}>
                    <span>{chipValue}</span>
                    <button
                      type="button"
                      onClick={() => onRemoveChip(chip)}
                      className={classes.chipRemove}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
          </div>

          {/* Active Saved Search Chip and Active Job Chip - Always on right */}
          {(activeSavedSearch || activeJob) && (
            <div className={classes.savedSearchChipContainer}>
              {activeSavedSearch && (
                <div className={classes.savedSearchChip}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={classes.savedSearchIcon}
                  >
                    <path
                      d="M12 1.6H4C3.11634 1.6 2.4 2.31634 2.4 3.2V12.8C2.4 13.6837 3.11634 14.4 4 14.4H12C12.8837 14.4 13.6 13.6837 13.6 12.8V3.2C13.6 2.31634 12.8837 1.6 12 1.6Z"
                      stroke="currentColor"
                      strokeWidth="1.2"
                    />
                    <path
                      d="M5.6 1.6V5.6H10.4V1.6"
                      stroke="currentColor"
                      strokeWidth="1.2"
                    />
                    <path
                      d="M5.6 10.4H10.4"
                      stroke="currentColor"
                      strokeWidth="1.2"
                    />
                  </svg>
                  <span>{activeSavedSearch.name}</span>
                  <button
                    type="button"
                    onClick={onClearActiveSavedSearch}
                    className={classes.chipRemove}
                    title={t('intelSearch.searchArea.clearSavedSearch')}
                  >
                    ×
                  </button>
                </div>
              )}
              {activeJob && (
                <div className={classes.savedSearchChip}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={classes.savedSearchIcon}
                  >
                    <path
                      d="M2.66667 4.66667V12C2.66667 12.7364 3.26359 13.3333 4 13.3333H12C12.7364 13.3333 13.3333 12.7364 13.3333 12V4.66667C13.3333 3.93029 12.7364 3.33333 12 3.33333H4C3.26359 3.33333 2.66667 3.93029 2.66667 4.66667Z"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M5.33333 3.33333V6.66667"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10.6667 3.33333V6.66667"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M2.66667 8H13.3333"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>{activeJob.title}</span>
                  <button
                    type="button"
                    onClick={onClearActiveJob}
                    className={classes.chipRemove}
                    title={t('intelSearch.searchArea.clearActiveJob')}
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Action Toolbar */}
      <div className={classes.actionToolbar}>
        {ENABLE_SELECTION && (
          <div className={classes.leftActions}>
            <div className={classes.actionDropdown}>
              <button
                type="button"
                ref={actionButtonRef}
                className={classes.actionButton}
                onClick={() => setActionMenuOpen(!actionMenuOpen)}
              >
                {t('intelSearch.searchArea.action')}
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 10L12 14L16 10"
                    stroke="#C1C5D6"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              {actionMenuOpen && (
                <div className={classes.actionMenu} ref={actionMenuRef}>
                  {actionOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={classes.actionMenuItem}
                      onClick={() => setActionMenuOpen(false)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <span className={classes.selectedCount}>
              {t('intelSearch.searchArea.recordsSelected', {
                count: selectedCandidates.length,
              })}
            </span>
          </div>
        )}

        <div className={classes.centerActions}>
          <span className={classes.resultsLabel}>
            {t('intelSearch.searchArea.searchResult')}:
          </span>
          <div className={classes.resultCount}>{pagination.totalResults}</div>
        </div>

        <div className={classes.rightActions}>
          <div className={classes.pagination}>
            <button
              type="button"
              className={classes.pageButton}
              onClick={() => {
                if (pagination.currentPage > 1) {
                  setPagination((prev) => ({
                    ...prev,
                    currentPage: prev.currentPage - 1,
                  }));
                }
              }}
              disabled={pagination.currentPage <= 1 || isLoading}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 18L9 12L15 6"
                  stroke="#242833"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <div className={classes.currentPage}>{pagination.currentPage}</div>
            <span className={classes.pageOf}>
              {t('intelSearch.searchArea.of')}
            </span>
            <span className={classes.totalPages}>{pagination.totalPages}</span>
            <button
              type="button"
              className={classes.pageButton}
              onClick={() => {
                if (pagination.currentPage < pagination.totalPages) {
                  setPagination((prev) => ({
                    ...prev,
                    currentPage: prev.currentPage + 1,
                  }));
                }
              }}
              disabled={
                pagination.currentPage >= pagination.totalPages || isLoading
              }
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 18L15 12L9 6"
                  stroke="#242833"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          <div className={classes.sortDropdown}>
            <button
              type="button"
              ref={sortButtonRef}
              className={classes.sortButton}
              onClick={() => setSortMenuOpen(!sortMenuOpen)}
            >
              {currentSortLabel}
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 10L12 14L16 10"
                  stroke="#C1C5D6"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            {sortMenuOpen && (
              <div className={classes.sortMenu} ref={sortMenuRef}>
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={classNames(classes.sortMenuItem, {
                      [classes.active]: sortBy === option.value,
                    })}
                    onClick={() => {
                      setSortBy(option.value);
                      setSortMenuOpen(false);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Results Scrollable Wrapper */}
      <div className={classes.resultsWrapper}>
        <div
          className={classNames(classes.resultsContainer, {
            [classes.twoColumn]: isLeftPanelCollapsed,
          })}
        >
          {isLoading && (
            <div className={classes.loadingState}>
              {t('intelSearch.searchArea.loading')}
            </div>
          )}
          {!isLoading &&
            results.length > 0 &&
            results.map((candidate) => (
              <CandidateCard
                key={candidate.id}
                candidate={candidate}
                isSelected={selectedCandidates.includes(candidate.id)}
                onToggle={() => onToggleCandidateSelection(candidate.id)}
              />
            ))}
          {!isLoading && results.length === 0 && (
            <div className={classes.emptyState}>
              {t('intelSearch.searchArea.noResults')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
